import { Check, Copy } from 'lucide-react';
import { useState, type MouseEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';

interface CopyableTextProps {
  /** Value copied to the clipboard when the icon is clicked. */
  text: string;
  /** Optional display content; defaults to `text` (e.g. a shortened label for a long id/url). */
  children?: React.ReactNode;
  /** Applied to the displayed text span — lets callers control size/weight/color per usage. */
  className?: string;
  /** Applied to the copy icon button. */
  iconClassName?: string;
  /** Toast message shown after a successful copy. */
  copyMessage?: string;
  /** Accessible name for the copy button. */
  ariaLabel?: string;
  /** `data-testid` root (default `copyable-text`); derives `${testId}-value` and `${testId}-copy-button`. */
  testId?: string;
}

/**
 * Displays text next to a click-to-copy icon. Text styling is fully
 * caller-controlled via `className`; the icon flips to a checkmark for 1.5s
 * as copy confirmation. Copies via `copyToClipboard`, which falls back to a
 * hidden-textarea + execCommand for Android/non-secure contexts.
 */
export function CopyableText({
  text,
  children,
  className,
  iconClassName,
  copyMessage = 'Copied to clipboard',
  ariaLabel = 'Copy',
  testId = 'copyable-text',
}: CopyableTextProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy(event: MouseEvent) {
    // Copying never also opens the clickable row or card the id sits in.
    event.stopPropagation();
    copyToClipboard(text);
    setCopied(true);
    toast.success(copyMessage);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  }

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5" data-testid={testId}>
      <span className={cn(className)} data-testid={`${testId}-value`}>
        {children ?? text}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={ariaLabel}
        data-testid={`${testId}-copy-button`}
        onClick={handleCopy}
        className={cn('h-6 w-6 shrink-0 cursor-pointer', iconClassName)}
      >
        {copied ? (
          <Check className="text-primary h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </Button>
    </span>
  );
}
